import { errorMessageFor } from './errorMessages.js'

/**
 * Thin fetch wrapper for the admin API surface. Handles JSON encoding,
 * cookie credentials, and turning the API's `{ error: { code, message } }`
 * shape into a typed AdminApiError whose `.userMessage` is ready to display.
 */

export class AdminApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: Record<string, unknown>

  constructor(
    status: number,
    code: string,
    message: string,
    details?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'AdminApiError'
    this.status = status
    this.code = code
    if (details !== undefined) this.details = details
  }

  /** Human string driven by the error-code dictionary. */
  get userMessage(): string {
    return errorMessageFor(this.code)
  }
}

interface ApiErrorShape {
  error?: { code?: string; message?: string; details?: Record<string, unknown> }
}

async function request<T>(
  path: string,
  init?: RequestInit & { json?: unknown },
): Promise<T> {
  const { json, headers, ...rest } = init ?? {}
  let res: Response
  try {
    res = await fetch(`/api/admin${path}`, {
      credentials: 'include',
      headers: {
        ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
      ...rest,
    })
  } catch {
    throw new AdminApiError(0, 'NETWORK_ERROR', 'Network request failed')
  }

  if (res.status === 204) return undefined as T

  let body: unknown = null
  const text = await res.text()
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = null
    }
  }

  if (!res.ok) {
    const err = (body as ApiErrorShape | null)?.error
    throw new AdminApiError(
      res.status,
      err?.code ?? 'UNKNOWN',
      err?.message ?? `Request failed (${res.status})`,
      err?.details,
    )
  }

  return body as T
}

export const adminApi = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, json?: unknown) =>
    request<T>(path, { method: 'POST', json }),
  patch: <T>(path: string, json?: unknown) =>
    request<T>(path, { method: 'PATCH', json }),
}
